import { Injectable, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, of, switchMap } from "rxjs";

// Service
import { SportService } from "@sports-services/sports.service";

// Models
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { C_Horses, C_League, C_LeagueCategory, eventDataModel } from "@sports-models/index";

// Stores
import { NewBaseService } from "@providers/newBase.service";
import { SidebarFacade } from "../sidebar/sidebar.facade";
import * as SportsbookActions from "./sportsbook.actions";
import { SportsbookState } from "./sportsbook.reducers";

@Injectable({ providedIn: "root" })
export class SportsbookFacade {
  store = inject(Store<SportsbookState>);
  sportService = inject(SportService);
  newBaseService = inject(NewBaseService);

  sidebarFacade = inject(SidebarFacade);

  sportsbook$: Observable<any>;
  sportsbook: SportsbookState;

  betsensePollingLaunched: boolean;
  selectedEvent: any;
  selectedRace: any;

  globals: GlobalVarsModel;

  constructor() {
    this.sportsbook$ = this.store.select((state) => state.sportsbook);
    this.newBaseService.getVars
      .pipe(
        switchMap((data: GlobalVarsModel) => {
          this.globals = data;
          return of(null);
        }),
      )
      .subscribe();
  }

  // Home Page
  initHomeData() {
    this.store.dispatch(SportsbookActions.initHome());
  }

  setHomeHighlighsSelectedSport(sport) {
    this.store.dispatch(SportsbookActions.setHomeHighlighsSelectedSport({ sport }));
  }

  restartingHomePollingTick(counter) {
    this.store.dispatch(SportsbookActions.restartingHomePollingTick({ counter }));
  }

  clearHome() {
    this.store.dispatch(SportsbookActions.clearHome());
  }

  // Sport Countries Page
  getSportCountriesData(sport: C_League) {
    this.store.dispatch(SportsbookActions.fetchSportCountries(sport));
  }
  clearSportCountries() {
    this.store.dispatch(SportsbookActions.clearSportCountries());
  }

  // Today Page
  getTodayData() {
    this.store.dispatch(SportsbookActions.fetchTodayCategories());
  }

  fetchTodayLeagueEvents(selectedEventsNodeIds: any[]) {
    this.store.dispatch(SportsbookActions.fetchTodayLeagueEvents({ selectedEventsNodeIds }));
  }

  setTodaySelectedDay(selectedDay) {
    this.store.dispatch(SportsbookActions.setTodaySelectedDay({ selectedDay }));
  }

  setTodaySelectedSport(sport: C_LeagueCategory) {
    this.store.dispatch(SportsbookActions.setTodaySelectedSport(sport));
  }

  clearToday() {
    this.store.dispatch(SportsbookActions.clearToday());
  }

  restoreTodayPageState(restoreState: any) {
    this.store.dispatch(SportsbookActions.restoreTodayPageState(restoreState));
  }

  // Event Page
  getEventData(eventData: eventDataModel) {
    this.store.dispatch(SportsbookActions.fetchEventMainCategories(eventData));
  }

  fetchEventGamesNoLive(market, league) {
    this.store.dispatch(SportsbookActions.fetchEventGamesNoLive({ market, league }));
  }

  setEventMarketSelectedCategory(eventMarketSelectedCategory) {
    this.store.dispatch(SportsbookActions.setEventMarketSelectedCategory(eventMarketSelectedCategory));
  }

  setEventMainSelectedCategory(eventMainSelectedCategory) {
    this.store.dispatch(SportsbookActions.setEventMainSelectedCategory(eventMainSelectedCategory));
  }

  clearEvent() {
    this.store.dispatch(SportsbookActions.clearEvent());
  }

  // Market Page
  getMarketData(selectedMarket) {
    this.store.dispatch(SportsbookActions.fetchMarketCategories(selectedMarket));
  }

  setMarketSelectedCategory(category) {
    this.store.dispatch(SportsbookActions.setMarketSelectedCategory(category));
  }

  fetchMarketBetsense(market) {
    this.betsensePollingLaunched = true;
    this.store.dispatch(SportsbookActions.fetchMarketBetsense(market));
  }

  clearMarket() {
    this.store.dispatch(SportsbookActions.clearMarkets());
  }

  // Race Page
  fetchRace(raceType, endOfToday) {
    this.selectedRace =
      raceType === C_Horses.races.type.horse ? C_Horses.races.name.horse : C_Horses.races.name.greyhound;
    this.store.dispatch(SportsbookActions.fetchRace({ raceType, endOfToday }));
  }

  clearRace() {
    this.store.dispatch(SportsbookActions.clearRace());
  }

  // Race Market Page
  setRaceMarketSelectedRace(race) {
    this.store.dispatch(SportsbookActions.setRaceMarketSelectedRace(race));
  }

  initRaceMarket(params) {
    this.selectedRace =
      params.SportHandle === C_Horses.races.type.horse ? C_Horses.races.name.horse : C_Horses.races.name.greyhound;
    this.store.dispatch(SportsbookActions.initRaceMarket(params));
  }

  fetchRaceMarket(nextRacesParams, completeRaceParams) {
    this.selectedRace =
      nextRacesParams.SportHandle === C_Horses.races.type.horse
        ? C_Horses.races.name.horse
        : C_Horses.races.name.greyhound;
    this.store.dispatch(SportsbookActions.fetchRaceMarket({ nextRacesParams, completeRaceParams }));
  }

  clearRaceMarket() {
    this.store.dispatch(SportsbookActions.clearRaceMarket());
  }

  // Live Page
  initLive() {
    this.store.dispatch(SportsbookActions.initLive());
  }

  fetchLive(payload) {
    this.store.dispatch(SportsbookActions.fetchLive(payload));
  }

  setSelectedSportsNav(selectedSport) {
    this.store.dispatch(SportsbookActions.setSelectedSportsNav(selectedSport));
  }

  setLiveSelectedCategory(payload) {
    this.store.dispatch(SportsbookActions.setLiveSelectedCategory(payload));
  }

  clearLive() {
    this.store.dispatch(SportsbookActions.clearLive());
  }

  // Live Market Page
  initLiveMarket(payload) {
    this.store.dispatch(SportsbookActions.initLiveMarket(payload));
  }

  fetchLiveMarketBetsense(payload) {
    this.store.dispatch(SportsbookActions.fetchLiveMarketBetsense(payload));
  }

  setLiveMarketSelectedCategory(payload) {
    this.store.dispatch(SportsbookActions.setLiveMarketSelectedCategory(payload));
  }

  setLiveMarket(payload) {
    this.store.dispatch(SportsbookActions.setLiveMarket(payload));
  }

  clearLiveMarket() {
    this.store.dispatch(SportsbookActions.clearLiveMarket());
  }

  // Navigation
  navigationUpdate(payload) {
    this.store.dispatch(SportsbookActions.navigationUpdate(payload));
  }

  historyUpdate(payload) {
    this.store.dispatch(SportsbookActions.historyUpdate(payload));
  }

  setSportsError(payload) {
    this.store.dispatch(SportsbookActions.setSportsError(payload));
  }

  consumeSportsError() {
    this.store.dispatch(SportsbookActions.consumeSportsError());
  }

  flushSportsErrorQueue() {
    this.store.dispatch(SportsbookActions.flushSportsErrorQueue());
  }
}
